import { engineComponent, NumberUtils } from "neo-common";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbol } from "../GameComponents/Symbol";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { Paylines } from "../GameComponents/Paylines";
import { Symbols } from "../GameComponents/Symbols";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { Settings } from "./Settings";
import { Tweens } from "../Components/Effects/Tweens";
import { Payline } from "../GameComponents/Payline";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { AppContext } from "../../Common/AppContext";
import { Languages } from "../Utils/Languages";
import { PaylinesDefinitions } from "../GameComponents/PaylinesDefinitions";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    //******************************************************************************************************************
    //  EMITED EVENTS
    //******************************************************************************************************************

    public static readonly FinishWinWithoutMysteryCalculation: string = "eventFinishWinWithoutMysteryCalculation";

    public static readonly FinishRespinWinAnimator: string = "eventFinishRespinWinAnimator";

    public static readonly MysteryStart: string = "eventMysteryStart";

    //******************************************************************************************************************
    //  TIMER STATUSES
    //******************************************************************************************************************

    protected static readonly ShowFirstWinsStep1 = "showFirstWinsStep1";

    protected static readonly ShowFirstWinsStep2 = "showFirstWinsStep2";

    protected static readonly ShowFirstWinsStep3 = "showFirstWinsStep3";

    protected static readonly ShowFirstWinsStep4 = "showFirstWinsStep4";

    protected static readonly ShowMysteryWinStep1 = "showMysteryWinStep1";

    protected static readonly ShowMysteryWinStep2 = "showMysteryWinStep2";

    protected static readonly ShowMysteryWinStep3 = "showMysteryWinStep3";

    protected static readonly ShowMysteryWinStep4 = "showMysteryWinStep4";

    protected static readonly StartCycleWin = "startCycleWin";

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    public static readonly RespinOutroDelay: number = 150;

    //wild settings
    private static readonly WildMoveDuration: number = 20;

    private static readonly BanditMoveDelay: number = 5;

    private static readonly SheriffMoveDelay: number = 5;

    //step win settings
    private static readonly ShowFirstWinDelay: number = 15;

    private static readonly WinStepDelay: number = 27; //15f symbol win anim + 12f max delayed win anim start

    private static readonly WinFinishDelay: number = 40;

    private static readonly RespinWinFinishDelay: number = 70;

    private static readonly CycleWinDelay: number = 40;

    //mystery win settings
    private static readonly MysteryStep2Delay: number = 60;

    private static readonly MysteryStepCounterDelay: number = 30;

    private static readonly MysteryFinishDelay: number = 180;

    private static readonly MysteryOutroDelay: number = 40;

    private static readonly MysteryMultipliers: Array<number> = [5, 50];

    private static readonly MysterySteps: number = 8;

    //wave payline win settings
    private static readonly DelayedTimesLeft: Array<number> = [0, 3, 6, 9, 12];

    private static readonly DelayedTimesRight: Array<number> = [12, 9, 6, 3, 0];

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected _initWinsFinisedTimer: FrameTimerEventEmitter;

    private currentCounterStep: number;

    private currentPaylineWin: number;

    private mysteryAnimation: TimelineElement;

    private mysteryCounter: SwitchElement;

    private wildsContainer: ContainerElement;

    private currentSheriffSymbols: Array<Array<Symbol>>;

    //includes also mystery
    private currentBanditSymbols: Array<Array<Symbol>>;

    private counter: SwitchElement;

    private paysBothWays: SwitchElement;

    private hasMystery: boolean;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this._initWinsFinisedTimer = CreateFrameTimer("InitWinsFinishedTimer");
        this._initWinsFinisedTimer.on(FrameTimerEvents.Complete, this.finishFirstInitWins.bind(this));
    }

    //******************************************************************************************************************
    //  INITIALIZE
    //******************************************************************************************************************

    public initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
    ): void {
        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        this.createCounter();
    }

    public initializeWilds(wildsContainer: ContainerElement, mysteryContainer: ContainerElement): void {
        this.wildsContainer = wildsContainer;
        this.currentSheriffSymbols = [];
        this.currentBanditSymbols = [];
        this.hasMystery = false;

        for (let i = 0; i < this.reelCount; i++) {
            this.currentSheriffSymbols.push([]);
            this.currentBanditSymbols.push([]);

            for (let j = 0; j < this.symbolCount; j++) {
                this.currentSheriffSymbols[i].push(null);
                this.currentBanditSymbols[i].push(null);
            }
        }
    }

    public initializeMystery(mysteryAnimator: ContainerElement): void {
        this.mysteryAnimation = <TimelineElement>mysteryAnimator.childs["MysteryTimeline"];
        this.mysteryCounter = <SwitchElement>mysteryAnimator.childs["MysteryCounter"];
    }

    public initializePaysBothWays(paysBothWays: SwitchElement): void {
        this.paysBothWays = paysBothWays;
    }

    public onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number): void {
        super.onReelStopped(reelsData, reelIndex);

        this.reorderWilds();

        if (reelsData[reelIndex].transform != null && (reelIndex == 0 || reelIndex == this.reelCount - 1)) {
            for (let i: number = 0; i < this.symbolCount; i++) {
                let symbol = this.getCurrentWildSymbol(reelIndex, i);

                if (symbol != null) {
                    let id = symbol.id + 1;

                    if (this.isImpact(id, reelIndex)) {
                        symbol.goToCase(Symbol.CaseImpact);

                        if (id == Settings.SymbolSheriff) {
                            PlayList.stop("SND_RespinWildReelLeft");
                            PlayList.play("SND_RespinSheriffHit");
                        } else {
                            PlayList.stop("SND_RespinWildReelRight");
                            PlayList.play("SND_RespinRogueHit");
                        }
                    }
                }
            }
        }
    }

    public showSymbols(reelsData: Array<Communication.Game.IReel>): void {
        super.showSymbols(reelsData);

        this.reorderWilds();
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    private reorderWilds(): void {
        for (let i: number = 0; i < this.symbolCount; i++) {
            //reorder sheriff
            for (let j: number = 0; j < this.reelCount; j++) {
                if (this.currentSheriffSymbols[j][i] != null) {
                    this.wildsContainer.addChild(this.currentSheriffSymbols[j][i]);
                }
            }

            //reorder bandit
            for (let j: number = this.reelCount - 1; j >= 0; j--) {
                if (
                    this.currentBanditSymbols[j][i] != null &&
                    this.currentBanditSymbols[j][i].id + 1 == Settings.SymbolBandit
                ) {
                    this.wildsContainer.addChild(this.currentBanditSymbols[j][i]);
                }
            }

            //reorder mystery
            for (let j: number = this.reelCount - 1; j >= 0; j--) {
                if (
                    this.currentBanditSymbols[j][i] != null &&
                    this.currentBanditSymbols[j][i].id + 1 == Settings.SymbolMystery
                ) {
                    this.wildsContainer.addChild(this.currentBanditSymbols[j][i]);
                } else if (
                    this.currentBanditSymbols[j][i] != null &&
                    this.currentBanditSymbols[j][i].id + 1 == Settings.SymbolBandit &&
                    this.currentBanditSymbols[j][i].getCurrentCase() == "CONFLICT_2"
                ) {
                    this.wildsContainer.addChild(this.currentBanditSymbols[j][i]);
                }
            }
        }
    }

    protected addCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        if (this.currentSymbols[reelIndex][symbolIndex] === null) {
            const id = this.currentReels[reelIndex].symbols[symbolIndex];
            const newWild = Settings.isWild(id);

            //impact wild into another wild to create mystery
            if (
                newWild &&
                this.currentReels[reelIndex].transform &&
                this.currentReels[reelIndex].transform[symbolIndex] == Settings.SymbolMystery
            ) {
                this.addCurrentWildSymbol(reelIndex, symbolIndex);

                return;
            }

            const symbol = this.symbols.getSymbol(id - 1, Symbol.CaseNormal);
            symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
            symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

            if (newWild) {
                this.wildsContainer.addChild(symbol);

                if (id == Settings.SymbolSheriff) {
                    this.currentSheriffSymbols[reelIndex][symbolIndex] = symbol;
                } else {
                    this.currentBanditSymbols[reelIndex][symbolIndex] = symbol;
                }
            } else {
                this.symbolsContainer.addChild(symbol);
                this.currentSymbols[reelIndex][symbolIndex] = symbol;
            }

            //refresh existing wilds on screen
            if (!newWild) {
                this.addCurrentWildSymbol(reelIndex, symbolIndex);
            }
        }
    }

    private addCurrentWildSymbol(reelIndex: number, symbolIndex: number): void {
        if (this.getCurrentWildSymbol(reelIndex, symbolIndex) == null) {
            if (this.currentReels[reelIndex].transform && this.currentReels[reelIndex].transform[symbolIndex] != -1) {
                const id = this.currentReels[reelIndex].transform[symbolIndex];
                const symbol = this.symbols.getSymbol(id - 1, Symbol.CaseNormal);
                symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
                symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * symbolIndex - symbol.offsetY;

                if (id == Settings.SymbolMystery) {
                    if (Settings.isWild(this.currentReels[reelIndex].symbols[symbolIndex])) {
                        symbol.goToCase(Symbol.CaseImpact);
                    }
                    this.hasMystery = true;
                }

                if (id == Settings.SymbolSheriff) {
                    this.currentSheriffSymbols[reelIndex][symbolIndex] = symbol;
                } else {
                    this.currentBanditSymbols[reelIndex][symbolIndex] = symbol;
                }

                this.wildsContainer.addChild(symbol);
            }
        }
    }

    protected removeCurrentSymbol(reelIndex: number, symbolIndex: number): void {
        if (this.currentSymbols[reelIndex][symbolIndex] !== null) {
            this.symbols.returnSymbol(this.currentSymbols[reelIndex][symbolIndex]);

            this.currentSymbols[reelIndex][symbolIndex] = null;
        }

        if (this.currentSheriffSymbols[reelIndex][symbolIndex] !== null) {
            this.symbols.returnSymbol(this.currentSheriffSymbols[reelIndex][symbolIndex]);

            this.currentSheriffSymbols[reelIndex][symbolIndex] = null;
        }

        if (this.currentBanditSymbols[reelIndex][symbolIndex] !== null) {
            this.symbols.returnSymbol(this.currentBanditSymbols[reelIndex][symbolIndex]);

            this.currentBanditSymbols[reelIndex][symbolIndex] = null;
        }
    }

    protected allSymbolsGoToReset(): void {
        const caseType = Symbol.CaseNormal;

        for (let i = 0; i < this.currentSymbols.length; i++) {
            // for (let j = 0; j < this.currentSymbols[i].length; j++) {
            for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    const currentCaseType = symbol.getCurrentCase();

                    if (caseType !== currentCaseType && currentCaseType != Symbol.CaseIdle) {
                        symbol.goToCase(caseType);
                    }

                    this.symbolsContainer.addChild(symbol);
                }

                if (!Settings.isRespin()) {
                    let wild = this.getCurrentWildSymbol(i, j);

                    if (wild) {
                        wild.goToCase(Symbol.CaseNormal);
                    }
                }
            }
        }
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    symbol.goToCase(caseType);

                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }
                }

                let wild = this.getCurrentWildSymbol(i, j);
                if (!noWin && caseType != Symbol.CaseEmpty && wild != null) {
                    wild.goToCase(caseType);
                }
            }
        }
    }

    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): Array<Symbol> {
        const win = this.currentWins[winIndex];
        let symbols: Array<Symbol> = [];

        switch (win.trigger) {
            case Communication.Game.WinTrigger.Payline:
                symbols = this.winSymbolsGoToCaseAsPayline(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Scatter:
                symbols = this.winSymbolsGoToCaseAsScatter(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Way:
                symbols = this.winSymbolsGoToCaseAsWay(winIndex, caseType);
                break;
            default:
                symbols = this.winSymbolsGoToCaseAsCustom(winIndex, caseType);
                break;
        }

        if (useValue && symbols.length > 0 && win.amount > 0) {
            this.value.visible = true;

            if (win.direction == Communication.Game.WinDirection.Left) {
                this.value.position.x = symbols[0].position.x + symbols[0].offsetX;
                this.value.position.y = symbols[0].position.y + symbols[0].offsetY;

                this.paysBothWays.goToCase("LEFT");
            } else {
                this.value.position.x = symbols[symbols.length - 1].position.x + symbols[symbols.length - 1].offsetX;
                this.value.position.y = symbols[symbols.length - 1].position.y + symbols[symbols.length - 1].offsetY;

                if (win.trigger == Communication.Game.WinTrigger.Scatter) {
                    this.paysBothWays.goToCase("BASE");
                } else {
                    this.paysBothWays.goToCase("RIGHT");
                }
            }

            if (Settings.isRespin()) {
                this.paysBothWays.goToCase("BASE");
            }

            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                this.value.play(parseInt(NumberUtils.devide(win.amount, BetManager.CoinSize).toString()));
            } else {
                ImageTextCounterElement.Round = false;
                this.value.play(parseFloat(Languages.formatText(Languages.FormatMoney, win.amount.toString())));
            }
        } else {
            this.value.visible = false;
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsPayline(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        if (this.paylines != null) {
            const definition = PaylinesDefinitions.getPaylineDefinitionsById(
                win.paylineId,
                this.paylines.paylineDefinition,
            );

            for (let i = 0; i < definition.length; i++) {
                if (win.mask[i] === 1 && this.allowSymbolGoToCase(i, definition[i], caseType)) {
                    let symbol = this.currentSymbols[i][definition[i]];
                    let isWild = false;
                    let delayedTimes =
                        win.direction == Communication.Game.WinDirection.Left
                            ? WinAnimatorExtended.DelayedTimesLeft
                            : WinAnimatorExtended.DelayedTimesRight;
                    let currentCaseType = caseType;

                    if (this.getCurrentWildSymbol(i, definition[i]) != null) {
                        symbol = this.getCurrentWildSymbol(i, definition[i]);
                        isWild = true;
                    }

                    if (currentCaseType == Symbol.CaseNormal + "_NOWIN") {
                        currentCaseType = Symbol.CaseNormal;

                        if (symbol.getCurrentCase() == Symbol.CaseWin + "_IDLE") {
                            currentCaseType = Symbol.CaseWin + "_IDLE";
                        }
                    }

                    if (currentCaseType == Symbol.CaseWin || currentCaseType == Symbol.CaseCycle) {
                        symbol.setDynamicTime(delayedTimes[i]);
                        symbol.goToCase(currentCaseType + "_DELAYED");
                    } else {
                        symbol.goToCase(currentCaseType);
                    }

                    if (!isWild) {
                        this.symbolsContainer.addChild(symbol);
                    }

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    let symbol = this.currentSymbols[i][j];
                    let isWild = false;

                    if (this.getCurrentWildSymbol(i, j) != null) {
                        symbol = this.getCurrentWildSymbol(i, j);
                        isWild = true;
                    }

                    symbol.goToCase(caseType);

                    if (!isWild) {
                        this.symbolsContainer.addChild(symbol);
                    }

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        this.currentWins = wins;
        this.currentWin = win;
        this.currentPaylineWin = this.getCurrentPaylineWin();

        if (this.currentPaylineWin == this.currentWin && this.hasMystery) {
            this.hasMystery = false;
        }

        this.resetAnimations();
        this.resetCounter();

        if (wins != null && wins.length > 0) {
            this.currentMultiplierIndex = this.getMultiplierIndex();

            if (!this.showCustomWin()) {
                this.showFirstWins();
            }

            return this.currentMultiplierIndex >= 3;
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());

            return false;
        }
    }

    protected showFirstWins(): void {
        if (this.currentMultiplierIndex < 3) {
            this.timer.stop();
            if (this.currentPaylineWin > 0) {
                this.currentCounterStep = 0;
                this.startTimer(WinAnimatorExtended.ShowFirstWinsStep1, WinAnimatorExtended.ShowFirstWinDelay);
            } else {
                this.startTimer(WinAnimatorExtended.ShowMysteryWinStep1, WinAnimatorExtended.ShowFirstWinDelay);
            }
        } else {
            this.allSymbolsGoToCase(Symbol.CaseNowin, true);
            this.allPaylinesGoToCase(Payline.CaseNowin);

            for (let i = 0; i < this.currentWins.length; i++) {
                if (this.currentWins[i].trigger != Communication.Game.WinTrigger.Scatter) {
                    this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
                    this.winPaylineGoToCase(i, Payline.CaseWin);
                }
            }

            this.startAnimation();
        }
    }

    protected finishFirstWins(): void {
        if (Settings.isRespin()) {
            this.emit(WinAnimatorExtended.FinishRespinWinAnimator);
        } else {
            this.emit(WinAnimator.FinishWinAnimator);
            this.startCycleWin();
        }
    }

    //******************************************************************************************************************
    //  SHOW STEP WINS
    //******************************************************************************************************************

    protected showFirstWinsStep1(): void {
        //if scatter stop step1 and go to step2
        if (this.currentWins[this.currentCounterStep].trigger == Communication.Game.WinTrigger.Scatter) {
            this.startTimer(WinAnimatorExtended.ShowFirstWinsStep2, WinAnimatorExtended.WinStepDelay);
            return;
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);

        this.keepShowedWinSymbolsActive();

        this.winPaylineGoToCase(this.currentCounterStep, Payline.CaseWin);
        this.winSymbolsGoToCase(this.currentCounterStep, Symbol.CaseWin, false);

        if (this.currentWins[this.currentCounterStep].direction == Communication.Game.WinDirection.Left) {
            PlayList.play("SND_BaseWinlineLeft");
        } else {
            PlayList.play("SND_BaseWinlineRight");
        }

        this.runCounterStep1();

        this.currentCounterStep++;

        const isLowWin = this.currentWin <= BetManager.Bet;
        const time = isLowWin ? 10 : WinAnimatorExtended.WinStepDelay;

        this.startTimer(
            this.currentCounterStep < this.currentWins.length
                ? WinAnimatorExtended.ShowFirstWinsStep1
                : WinAnimatorExtended.ShowFirstWinsStep2,
            time,
        );
    }

    private keepShowedWinSymbolsActive(): void {
        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].trigger != Communication.Game.WinTrigger.Scatter) {
                if (i < this.currentCounterStep) {
                    this.winSymbolsGoToCase(i, Symbol.CaseWin + "_IDLE", false);

                    if (this.currentWins[i].direction == Communication.Game.WinDirection.Right) {
                        this.winPaylineGoToCase(i, Payline.CaseWin + "_IDLE_RIGHT");
                    } else {
                        this.winPaylineGoToCase(i, Payline.CaseWin + "_IDLE");
                    }
                } else {
                    this.winSymbolsGoToCase(i, Symbol.CaseNormal + "_NOWIN", false);

                    if (this.currentWins[i].direction == Communication.Game.WinDirection.Right) {
                        this.winPaylineGoToCase(i, Payline.CaseCycle + "_IDLE_RIGHT");
                    } else {
                        this.winPaylineGoToCase(i, Payline.CaseCycle + "_IDLE_LEFT");
                    }
                }
            }
        }
    }

    protected showFirstWinsStep2(): void {
        this.currentCounterStep = null;

        this.runCounterStep2();

        if (Settings.isRespin()) {
            this.startTimer(WinAnimatorExtended.ShowFirstWinsStep3, WinAnimatorExtended.RespinWinFinishDelay);
        } else {
            this.startTimer(WinAnimatorExtended.ShowFirstWinsStep3, WinAnimatorExtended.WinFinishDelay);
        }

        if (this.hasMystery) {
            this.emit(WinAnimatorExtended.FinishWinWithoutMysteryCalculation);
        } else {
            this.emit(WinAnimator.FinishWinCalculation);
        }
    }

    protected showFirstWinsStep3(): void {
        let delay = WinAnimatorExtended.CycleWinDelay;

        if (Settings.InRespin) {
            delay = delay * 0.6;
        }

        this.runCounterStep3();

        if (this.hasMystery) {
            this.startTimer(WinAnimatorExtended.ShowMysteryWinStep1, WinAnimatorExtended.CycleWinDelay);
        } else {
            this.startTimer(WinAnimatorExtended.ShowFirstWinsStep4, delay);
        }
    }

    protected showFirstWinsStep4(): void {
        if (Settings.InRespin) {
            this.emit(WinAnimatorExtended.FinishRespinWinAnimator);
        } else {
            this.emit(WinAnimator.FinishWinAnimator);
        }

        if (this.currentWins != null && !Settings.isRespin()) {
            if (Settings.InRespin) {
                this.startTimer(WinAnimatorExtended.StartCycleWin, WinAnimatorExtended.RespinOutroDelay);
            } else {
                this.startCycleWin();
            }
        }
    }

    //******************************************************************************************************************
    //  CYCLE WINS
    //******************************************************************************************************************

    private startCycleWin(): void {
        this.stopTimer();

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winSymbolsGoToCase(i, Symbol.CaseWin + "_RESET", false);
        }

        this.currentCycle = 0;
        this.cycleWins();
    }

    protected cycleWins(): void {
        if (this.currentWins == null) {
            return;
        }

        this.allPaylinesGoToCase(Payline.CaseNowin);

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].direction == Communication.Game.WinDirection.Right) {
                this.winPaylineGoToCase(i, Payline.CaseCycle + "_IDLE_RIGHT");
            } else {
                this.winPaylineGoToCase(i, Payline.CaseCycle + "_IDLE_LEFT");
            }
        }

        this.winSymbolsGoToCase(this.currentCycle, Symbol.CaseCycle, true);
        this.winPaylineGoToCase(this.currentCycle, Payline.CaseCycle);

        this.currentCycle = (this.currentCycle + 1) % this.currentWins.length;

        this.startTimer(WinAnimator.CycleWins, this.getCycleWinsDelay());
    }

    //******************************************************************************************************************
    //  SHOW MYSTERY WINS
    //******************************************************************************************************************

    private showMysteryWinStep1(): void {
        this.emit(WinAnimatorExtended.MysteryStart);

        this.allPaylinesGoToCase(Payline.CaseNowin);
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].trigger != Communication.Game.WinTrigger.Scatter) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin + "_IDLE", false);

                if (this.currentWins[i].direction == Communication.Game.WinDirection.Right) {
                    this.winPaylineGoToCase(i, Payline.CaseWin + "_IDLE_RIGHT");
                } else {
                    this.winPaylineGoToCase(i, Payline.CaseWin + "_IDLE");
                }
            }
        }

        this.currentCounterStep = 0;
        this.mysteryAnimation.play();

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].trigger == Communication.Game.WinTrigger.Scatter) {
                this.winSymbolsGoToCase(i, Symbol.CaseWin, false);
            }
        }

        this.startTimer(WinAnimatorExtended.ShowMysteryWinStep2, WinAnimatorExtended.MysteryStep2Delay);
    }

    private showMysteryWinStep2(): void {
        this.runMysteryCounterStep2();
        this.currentCounterStep++;

        let nextPhase = WinAnimatorExtended.ShowMysteryWinStep2;
        let nextDelay = WinAnimatorExtended.MysteryStepCounterDelay;

        if (this.currentCounterStep == WinAnimatorExtended.MysterySteps) {
            nextPhase = WinAnimatorExtended.ShowMysteryWinStep3;
            nextDelay = nextDelay * 2;
        }

        this.startTimer(nextPhase, nextDelay);
    }

    private showMysteryWinStep3(): void {
        this.currentCounterStep = null;

        this.runMysteryCounterStep3();

        this.startTimer(WinAnimatorExtended.ShowMysteryWinStep4, WinAnimatorExtended.MysteryFinishDelay);
        this.emit(WinAnimator.FinishWinCalculation);
    }

    private showMysteryWinStep4(): void {
        this.currentCounterStep = null;

        this.mysteryAnimation.goToOutro();
        this.runMysteryCounterStepOutro();

        this.startTimer(WinAnimatorExtended.ShowFirstWinsStep4, WinAnimatorExtended.MysteryOutroDelay);
    }

    //******************************************************************************************************************
    //  SHOW INIT WINS
    //******************************************************************************************************************

    public showInitWins(wins: Array<Communication.Game.IWin>, win: number): void {
        this.resetCounter();

        this.currentWins = wins;
        this.currentWin = win;

        this.resetAnimations();

        if (wins != null && wins.length > 0) {
            this.showCustomInitWin();
            this.showFirstInitWins();

            if (!Settings.isRespin()) {
                // this.finishFirstInitWins();
                this._initWinsFinisedTimer.start(120);
            }
        } else {
            this.startTimer(WinAnimator.Idle, this.getInitialIdleDelay());
        }
    }

    //******************************************************************************************************************
    //  PAYLINES
    //******************************************************************************************************************

    protected winPaylineGoToCase(winIndex: number, caseType: string): void {
        if (this.paylines != null) {
            const win = this.currentWins[winIndex];

            if (win.trigger === Communication.Game.WinTrigger.Payline) {
                if (caseType === Payline.CaseCycle && this.currentWins.length === 1) {
                    if (win.direction === Communication.Game.WinDirection.Left) {
                        this.paylines.goToCase(Payline.CaseSingleCycle, win.paylineId);
                    } else {
                        this.paylines.goToCase(Payline.CaseSingleCycle + "_RIGHT", win.paylineId);
                    }
                } else {
                    if (caseType == Payline.CaseWin || caseType == Payline.CaseCycle) {
                        if (win.direction === Communication.Game.WinDirection.Left) {
                            this.paylines.goToCase(caseType, win.paylineId);
                        } else {
                            this.paylines.goToCase(caseType + "_RIGHT", win.paylineId);
                        }
                    } else {
                        this.paylines.goToCase(caseType, win.paylineId);
                    }
                }
            }
        }
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean): boolean {
        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep1:
                this.runCounterOutro();
                break;
            case WinAnimatorExtended.ShowFirstWinsStep2:
            case WinAnimatorExtended.ShowFirstWinsStep3:
                this.runCounterStep3();
                break;
        }

        if (this.currentCounterStep != null) {
            this.currentCounterStep = null;
            this.runCounterOutro();
        }

        this.hasMystery = false;

        return super.reset(showLastSymbols);
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected stopTimer(): void {
        if (this.timer != null) {
            this.timer.stop();
        }

        if (this._initWinsFinisedTimer != null) {
            this._initWinsFinisedTimer.stop();
        }
    }

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep1:
                this.showFirstWinsStep1();
                break;
            case WinAnimatorExtended.ShowFirstWinsStep2:
                this.showFirstWinsStep2();
                break;
            case WinAnimatorExtended.ShowFirstWinsStep3:
                this.showFirstWinsStep3();
                break;
            case WinAnimatorExtended.ShowFirstWinsStep4:
                this.showFirstWinsStep4();
                break;
            case WinAnimatorExtended.ShowMysteryWinStep1:
                this.showMysteryWinStep1();
                break;
            case WinAnimatorExtended.ShowMysteryWinStep2:
                this.showMysteryWinStep2();
                break;
            case WinAnimatorExtended.ShowMysteryWinStep3:
                this.showMysteryWinStep3();
                break;
            case WinAnimatorExtended.ShowMysteryWinStep4:
                this.showMysteryWinStep4();
                break;
            case WinAnimatorExtended.StartCycleWin:
                this.startCycleWin();
                break;
            default:
                super.onTimerComplete();
                break;
        }
    }

    //******************************************************************************************************************
    //  COUNTER
    //******************************************************************************************************************

    protected createCounter(): void {
        this.counter = <SwitchElement>this.childs["Counter"];

        this.animationsContainer.addChild(this.counter);
    }

    protected resetCounter(): void {
        Tweens.removeTweens(this.counter, "all");
        this.counter.alpha = 0;
        this.counter.goToCase("EMPTY");
    }

    protected runCounterStep1(): void {
        PlayList.play("SND_BaseWinline" + Math.min(this.currentCounterStep + 1, 9));

        const isLowWin = this.currentWin <= BetManager.Bet;
        const tweenDuration = isLowWin ? 0 : 20;
        const alphaTweenDuration = isLowWin ? 10 : 20;
        const counterDuration = isLowWin ? 0 : 1;

        let amountFrom = "0";
        let amountTo = "0";
        let counterElement = <ImageTextCounterElement>this.counter.getChildElementByName("Counter");

        for (let i = 0; i <= this.currentCounterStep; i++) {
            const win = this.currentWins[i];

            if (win.type === Communication.Game.WinType.Cash) {
                if (i < this.currentCounterStep) {
                    amountFrom = NumberUtils.add(amountFrom, this.currentWins[i].amount);
                }

                amountTo = NumberUtils.add(amountTo, this.currentWins[i].amount);
            }
        }

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            counterElement.start(
                parseInt(NumberUtils.devide(amountFrom, BetManager.CoinSize).toString()),
                parseInt(NumberUtils.devide(amountTo, BetManager.CoinSize).toString()),
                WinAnimatorExtended.WinStepDelay * 0.5,
            );
        } else {
            ImageTextCounterElement.Round = false;
            counterElement.start(
                parseFloat(amountFrom),
                parseFloat(amountTo),
                WinAnimatorExtended.WinStepDelay * 0.5 * counterDuration,
            );
        }

        const step = (0.8 - 0.6) / this.currentWins.length;

        this.counter.goToCase("PLAY");

        Tweens.removeTweens(this.counter, "all");
        Tweens.alphaTween(this.counter, {
            alphas: [this.counter.alpha, 1],
            tweenDuration: alphaTweenDuration,
        });
        Tweens.scaleTween(this.counter, {
            tweenValues: [
                new PIXI.Point(0.6 + step * this.currentCounterStep, 0.6 + step * this.currentCounterStep),
                new PIXI.Point(0.6 + step * (this.currentCounterStep + 1), 0.6 + step * (this.currentCounterStep + 1)),
            ],
            tweenDuration: tweenDuration,
            tweenEase: "backInOut",
        });
    }

    protected runCounterStep2(): void {
        PlayList.play("SND_BaseWin" + (this.currentMultiplierIndex + 1));
        // PlayList.play('SND_BaseWin01');

        Tweens.removeTweens(this.counter, "all");
        Tweens.scaleTween(this.counter, {
            tweenValues: [new PIXI.Point(0.8, 0.8), new PIXI.Point(0.96, 0.96), new PIXI.Point(0.8, 0.8)],
            tweenDuration: 30,
        });
    }

    protected runCounterStep3(): void {
        PlayList.play("SND_BaseWinAway");

        Tweens.removeTweens(this.counter, "all");
        Tweens.alphaTween(this.counter, {
            alphas: [1, 0],
            tweenDuration: 24,
        });
        Tweens.scaleTween(this.counter, {
            tweenValues: [new PIXI.Point(0.8, 0.8), new PIXI.Point(1.44, 1.44)],
            tweenDuration: 24,
        });
    }

    protected runCounterOutro(): void {
        PlayList.play("SND_BaseWin" + (this.currentMultiplierIndex + 1));
        // PlayList.play('SND_BaseWin01');

        let counterElement = <ImageTextCounterElement>this.counter.getChildElementByName("Counter");

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            counterElement.start(0, parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()), 0);
        } else {
            ImageTextCounterElement.Round = false;
            counterElement.start(0, this.currentWin, 0);
        }

        this.counter.goToCase("PLAY");

        Tweens.removeTweens(this.counter, "all");
        Tweens.alphaTween(this.counter, {
            alphas: [1, 0],
            tweenDuration: 20,
            tweenDelay: 20,
        });
        Tweens.scaleTween(this.counter, {
            tweenValues: [new PIXI.Point(0.8, 0.8), new PIXI.Point(1.44, 1.44)],
            tweenDuration: 20,
            tweenDelay: 20,
        });
    }

    //******************************************************************************************************************
    //  MYSTERY COUNTER
    //******************************************************************************************************************

    //MysteryStep1 is just initialization
    private runMysteryCounterStep2(): void {
        let amount = 0;
        let counter = <ImageTextCounterElement>this.mysteryCounter.getChildElementByName("CounterStep");
        let caseType = "PLAY";

        //final step
        if (this.currentCounterStep == WinAnimatorExtended.MysterySteps - 1) {
            amount = parseFloat(NumberUtils.subtract(this.currentWin, this.currentPaylineWin));
            caseType = "PLAY_FINAL";
            counter = <ImageTextCounterElement>this.mysteryCounter.getChildElementByName("Counter");
        } else if (this.currentCounterStep == WinAnimatorExtended.MysterySteps - 2) {
            //last step before final
            let min = WinAnimatorExtended.MysteryMultipliers[0];
            let max = parseFloat(NumberUtils.subtract(this.currentWin, this.currentPaylineWin));
            max = parseFloat(NumberUtils.devide(max, BetManager.Bet));
            max = parseFloat(NumberUtils.devide(max, this.getMysterySymbolsCount())) - 1;

            //in case the final amount is the lowest possible, show last random number higher than final
            if (min == max + 1) {
                min *= 1.2; //possible multipliers 6,12,18
                max = max * 2 + 1;
            }

            amount = Math.floor(Math.random() * (max - min + 1)) + min;
            amount = amount * this.getMysterySymbolsCount() * BetManager.Bet;

            PlayList.play("SND_RespinMysteryWinline" + ((this.currentCounterStep % 3) + 1));
        } else {
            amount =
                Math.floor(
                    Math.random() *
                        (WinAnimatorExtended.MysteryMultipliers[1] - WinAnimatorExtended.MysteryMultipliers[0] + 1),
                ) + WinAnimatorExtended.MysteryMultipliers[0];
            amount = amount * this.getMysterySymbolsCount() * BetManager.Bet;

            PlayList.play("SND_RespinMysteryWinline" + ((this.currentCounterStep % 3) + 1));
        }

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            counter.start(0, parseInt(NumberUtils.devide(amount, BetManager.CoinSize).toString()), 0);
        } else {
            ImageTextCounterElement.Round = false;
            counter.start(0, amount, 0);
        }

        this.mysteryCounter.goToCase(caseType);
    }

    private runMysteryCounterStep3(): void {
        this.mysteryCounter.goToCase("WIN");
    }

    private runMysteryCounterStepOutro(): void {
        PlayList.play("SND_BaseWinAway");

        this.mysteryCounter.goToCase("OUTRO");
    }

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    protected startAnimation(): void {
        this.currentAnimationIndex = this.getAnimationIndex();
        this.currentAnimationFinished = false;

        const animation = this.animations[this.currentAnimationIndex];
        animation.visible = true;

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(parseInt(NumberUtils.devide(this.currentPaylineWin, BetManager.CoinSize).toString()));
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(this.currentPaylineWin);
        }
    }

    protected stopAnimation(): void {
        if (this.currentAnimationIndex != null) {
            const animation = this.animations[this.currentAnimationIndex];
            animation.visible = true;
            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                animation.goToOutro(
                    parseInt(NumberUtils.devide(this.currentPaylineWin, BetManager.CoinSize).toString()),
                );
            } else {
                ImageTextCounterElement.Round = false;
                animation.goToOutro(this.currentPaylineWin);
            }

            this.currentAnimationIndex = null;
            this.currentAnimationFinished = true;
        }
    }

    protected getAnimationIndex(): number {
        return this.currentMultiplierIndex - 3;
    }

    protected onAnimationFinished(): void {
        this.currentAnimationFinished = true;

        if (this.hasMystery) {
            this.emit(WinAnimatorExtended.FinishWinWithoutMysteryCalculation);
        } else {
            this.emit(WinAnimator.FinishWinCalculation);
        }
    }

    protected onAnimationComplete(): void {
        this.currentAnimationIndex = null;
        this.currentMultiplierIndex = -1;

        if (this.hasMystery) {
            this.startTimer(WinAnimatorExtended.ShowMysteryWinStep1, WinAnimatorExtended.ShowFirstWinDelay);
        } else {
            this.finishFirstWins();
        }
    }

    //******************************************************************************************************************
    //  IDLE
    //******************************************************************************************************************

    protected idle(): void {
        for (let i = 0; i < this.getIdleActiveElementsCount(); i++) {
            const symbol =
                this.currentSymbols[Math.floor(Math.random() * this.reelCount)][
                    Math.floor(Math.random() * this.symbolCount)
                ];

            if (symbol != null && symbol.getCurrentCase() === Symbol.CaseNormal) {
                if (symbol.hasCase(Symbol.CaseIdle)) {
                    symbol.goToCase(Symbol.CaseIdle);
                }
            }
        }

        this.startTimer(WinAnimator.Idle, this.getIdleDelay());
    }

    //******************************************************************************************************************
    //  WILDS
    //******************************************************************************************************************

    public addLimitWildsToReels(): void {
        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                let wild = this.getCurrentWildSymbol(i, j);

                if (wild) {
                    let symbol = this.currentSymbols[i][j];

                    if (symbol) {
                        this.symbols.returnSymbol(symbol);
                    }

                    this.currentSymbols[i][j] = wild;

                    if (wild.id + 1 == Settings.SymbolBandit) {
                        this.currentBanditSymbols[i][j] = null;
                    } else {
                        this.currentSheriffSymbols[i][j] = null;
                    }
                }
            }
        }
    }

    public moveWilds(): void {
        if (this.wildsContainer.children.length > 0) {
            let mysteryArray: Array<Symbol> = [];

            for (let i = 0; i < this.reelCount; i++) {
                let reverseIndex = this.reelCount - i - 1;
                for (let j = 0; j < this.symbolCount; j++) {
                    this.moveSheriffsToRight(reverseIndex, j);
                    let mystery = this.moveBanditsToLeft(i, j);

                    if (mystery) {
                        mysteryArray.unshift(mystery);
                    }
                }
            }

            this.reorderWilds();

            for (let i = 0; i < mysteryArray.length; i++) {
                this.wildsContainer.addChild(mysteryArray[i]);
            }
        }
    }

    private moveBanditsToLeft(reelIndex: number, rowIndex: number): Symbol {
        //if mystery, we need to create bandit symbol and move
        if (
            this.currentReels[reelIndex].transform &&
            (this.currentReels[reelIndex].transform[rowIndex] == Settings.SymbolMystery ||
                this.currentReels[reelIndex].transform[rowIndex] == Settings.SymbolBandit)
        ) {
            let mystery: Symbol = null;
            let symbol = this.currentBanditSymbols[reelIndex][rowIndex];
            let caseType = "MOVE";
            let nextReelIndex = reelIndex - 1;
            let fadeOut = reelIndex == 0;
            let missMove = false;

            if (symbol.id + 1 == Settings.SymbolMystery) {
                mystery = symbol;
                symbol = this.symbols.getSymbol(Settings.SymbolBandit - 1, Symbol.CaseNormal);
                symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
                symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * rowIndex - symbol.offsetY;
                this.wildsContainer.addChild(symbol);
            }

            if (nextReelIndex > -1) {
                if (this.hasWildSymbol(nextReelIndex, rowIndex, Settings.SymbolSheriff)) {
                    missMove = true;
                }

                if (this.hasWildSymbol(nextReelIndex - 1, rowIndex, Settings.SymbolSheriff)) {
                    caseType = "CONFLICT";
                }
            } else {
                caseType = "MOVE_OUT";
            }

            this.currentBanditSymbols[reelIndex][rowIndex] = null;
            if (!fadeOut) {
                this.currentBanditSymbols[reelIndex - 1][rowIndex] = symbol;
            }

            this.tweenWild(symbol, WinAnimatorExtended.BanditMoveDelay, -1, caseType, fadeOut, missMove, mystery);

            return mystery;
        }

        return null;
    }

    private moveSheriffsToRight(reelIndex: number, rowIndex: number): void {
        //if mystery, we need to create sheriff symbol and move
        if (
            this.currentReels[reelIndex].transform &&
            (this.currentReels[reelIndex].transform[rowIndex] == Settings.SymbolMystery ||
                this.currentReels[reelIndex].transform[rowIndex] == Settings.SymbolSheriff)
        ) {
            let symbol = this.currentSheriffSymbols[reelIndex][rowIndex];
            let caseType = "MOVE";
            let nextReelIndex = reelIndex + 1;
            let fadeOut = reelIndex == this.reelCount - 1;
            let missMove = false;

            if (symbol == null) {
                symbol = this.symbols.getSymbol(Settings.SymbolSheriff - 1, Symbol.CaseNormal);
                symbol.position.x = this.reelX[reelIndex] - symbol.offsetX;
                symbol.position.y = this.reelY[reelIndex] + this.symbolHeight * rowIndex - symbol.offsetY;
                this.wildsContainer.addChild(symbol);
            }

            if (nextReelIndex < this.reelCount) {
                if (this.hasWildSymbol(nextReelIndex, rowIndex, Settings.SymbolBandit)) {
                    missMove = true;
                }

                if (this.hasWildSymbol(nextReelIndex + 1, rowIndex, Settings.SymbolBandit)) {
                    caseType = "CONFLICT";
                }
            } else {
                caseType = "MOVE_OUT";
            }

            this.currentSheriffSymbols[reelIndex][rowIndex] = null;
            if (!fadeOut) {
                this.currentSheriffSymbols[reelIndex + 1][rowIndex] = symbol;
            }

            this.tweenWild(symbol, WinAnimatorExtended.SheriffMoveDelay, 1, caseType, fadeOut, missMove);
        }
    }

    private tweenWild(
        wild: Symbol,
        delay: number,
        direction: number,
        caseType: string,
        fadeOut: boolean,
        missMove: boolean,
        mystery: Symbol = null,
    ): void {
        let distance = this.symbolWidth * direction;

        let yOffset = parseInt(NumberUtils.devide(this.symbolHeight, 8)) * direction;
        if (wild.id + 1 == Settings.SymbolSheriff) {
            yOffset *= 2; //there is no pivot in symbol, if we applied scale, it doesnt move down, so we multiply it by 2
        }

        let tweenEase = "wildMove";
        if (missMove) {
            tweenEase = "wildMove2";
        }

        Tweens.removeTweens(wild);
        let event = Tweens.customTween(wild, {
            tween: "position.x",
            tweenValues: [wild.x, wild.x + distance],
            tweenDuration: [WinAnimatorExtended.WildMoveDuration],
            tweenDelay: [delay],
            tweenEase: [tweenEase],
        });

        if (missMove) {
            Tweens.customTween(wild, {
                tween: "position.y",
                tweenValues: [wild.y, wild.y + yOffset],
                tweenDuration: [WinAnimatorExtended.WildMoveDuration],
                tweenDelay: [delay],
                tweenEase: ["missMove"],
            });

            if (wild.id + 1 == Settings.SymbolSheriff) {
                Tweens.customTween(wild, {
                    tween: "scale.x",
                    tweenValues: [1, 0.7],
                    tweenDuration: [WinAnimatorExtended.WildMoveDuration],
                    tweenDelay: [delay],
                    tweenEase: ["missMove"],
                });

                Tweens.customTween(wild, {
                    tween: "scale.y",
                    tweenValues: [1, 0.7],
                    tweenDuration: [WinAnimatorExtended.WildMoveDuration],
                    tweenDelay: [delay],
                    tweenEase: ["missMove"],
                });
            }
        }

        event.eventCallback("onStart", this.onTweenWildStart, [wild, caseType, direction, fadeOut, mystery], this);
        event.eventCallback("onComplete", this.onTweenWildComplete, [wild, caseType, fadeOut, mystery], this);
    }

    private onTweenWildStart(
        wild: Symbol,
        caseType: string,
        direction: number,
        fadeOut: boolean,
        mystery: Symbol,
    ): void {
        let moveSound = "SND_RespinRogueMoves";
        if (wild.id + 1 == Settings.SymbolSheriff) {
            moveSound = "SND_RespinSheriffMoves";
        }

        if (fadeOut) {
            if (direction == 1) {
                moveSound = "SND_RespinWildOutRight";
            } else {
                moveSound = "SND_RespinWildOutLeft";
            }
        }

        if (mystery != null) {
            mystery.goToCase("OUT");
        }

        PlayList.play(moveSound);

        if (wild.getCurrentCase() != Symbol.CaseEmpty) {
            wild.goToCase(caseType);
        }
    }

    private onTweenWildComplete(wild: Symbol, caseType: string, fadeOut: boolean, mystery: Symbol = null): void {
        if (caseType == "CONFLICT") {
            let id = wild.id + 1;

            if (id == Settings.SymbolSheriff) {
                PlayList.play("SND_RespinSheriffHit");
            } else {
                this.reorderWilds();
                PlayList.play("SND_RespinRogueHit");
            }
        }

        if (mystery != null) {
            this.symbols.returnSymbol(mystery);
        }

        if (fadeOut) {
            this.symbols.returnSymbol(wild);
        }
    }

    public getWildStatus(): number {
        let hasBandit = false;
        let hasSheriff = false;

        for (let j = 0; j < this.currentSheriffSymbols[0].length; j++) {
            let inRow = false;

            for (let i = 0; i < this.currentBanditSymbols.length; i++) {
                if (this.currentSheriffSymbols[i][j] != null) {
                    inRow = true;
                    hasSheriff = true;
                } else if (this.currentBanditSymbols[i][j] != null) {
                    if (this.currentBanditSymbols[i][j].id + 1 == Settings.SymbolMystery) {
                        return 3;
                    } else if (inRow) {
                        for (let k = i - 2; k > -1; k -= 2) {
                            if (this.currentSheriffSymbols[k][j] != null) {
                                return 3;
                            }
                        }
                    }
                    hasBandit = true;
                }
            }
        }

        if (hasBandit && hasSheriff) {
            return 2;
        } else if (hasBandit) {
            return 1;
        } else if (hasSheriff) {
            return 0;
        }

        return -1;
    }

    //******************************************************************************************************************
    //  HELPER FUNCTIONS
    //******************************************************************************************************************

    private isImpact(id: number, reelIndex: number): boolean {
        return (
            (reelIndex == 0 && id == Settings.SymbolSheriff) ||
            (reelIndex == this.reelCount - 1 && id == Settings.SymbolBandit)
        );
    }

    private getCurrentPaylineWin(): number {
        let currentWin = this.currentWin;

        for (let i = 0; i < this.currentWins.length; i++) {
            if (this.currentWins[i].trigger == Communication.Game.WinTrigger.Scatter) {
                currentWin = parseFloat(NumberUtils.subtract(currentWin, this.currentWins[i].amount));
            }
        }

        return currentWin;
    }

    private getMultiplierIndex(): number {
        let multiplierIndex = 0;

        for (let i = 0; i < this.multipliers.length; i++) {
            if (this.currentPaylineWin / BetManager.Bet >= this.multipliers[i]) {
                multiplierIndex = i;
            }
        }

        return multiplierIndex;
    }

    private getCurrentWildSymbol(reelIndex: number, symbolIndex: number): Symbol {
        return this.currentSheriffSymbols[reelIndex][symbolIndex] || this.currentBanditSymbols[reelIndex][symbolIndex];
    }

    private getMysterySymbolsCount(): number {
        let mysteryCount = 0;

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if (
                    this.currentBanditSymbols[i][j] != null &&
                    this.currentBanditSymbols[i][j].id + 1 == Settings.SymbolMystery
                ) {
                    mysteryCount++;
                }
            }
        }

        return mysteryCount;
    }

    private hasWildSymbol(reelIndex: number, rowIndex: number, wildId: number): boolean {
        if (reelIndex < this.reelCount && reelIndex > -1) {
            if (
                this.currentReels[reelIndex].transform &&
                (this.currentReels[reelIndex].transform[rowIndex] == wildId ||
                    this.currentReels[reelIndex].transform[rowIndex] == Settings.SymbolMystery)
            ) {
                return true;
            }
        }
        return false;
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/